/** Completed marks change by replacement; their coordinates need no Vue observers. */
export function freezePdfMark(mark) {
    return Object.freeze({ ...mark, points: Object.freeze(mark.points.map((point) => Object.freeze([...point]))) });
}

export function pdfCalloutRadius(mark) { return (mark.size || 12) * Math.max(.9, (mark.text?.length || 1)*.28+.25); }

/** Project PDF user coordinates identically on the full page and thumbnails. */
export function projectPdfMarks(annotations, viewport, page, selected = -1, draft = null) {
    if (!viewport) return [];
    const marks = [];
    annotations.forEach((mark, index) => { if (mark.page === page) marks.push({ ...mark, index }); });
    if (draft?.page === page) marks.push({ ...draft, index: -1 });
    return marks.map((mark) => {
        const points = mark.points.map((point) => viewport.convertToViewportPoint(...point));
        const xs = points.map((point) => point[0]), ys = points.map((point) => point[1]);
        const radius = mark.kind === 'callout' ? pdfCalloutRadius(mark)*viewport.scale : 0;
        const x = Math.min(...xs,xs[0]-radius), y = Math.min(...ys,ys[0]-radius);
        const width = Math.max(...xs,xs[0]+radius) - x, height = Math.max(...ys,ys[0]+radius) - y;
        const angle = ((viewport.rotation || 0) - (mark.rotation || 0) + 360) % 360;
        const sideways = angle === 90 || angle === 270;
        return { ...mark, selected: mark.index === selected, x, y, width, height, radius, angle,
            startX: xs[0], startY: ys[0], endX: xs[xs.length-1], endY: ys[ys.length-1],
            textX: angle ? -(sideways ? height : width) / 2 : x,
            textY: angle ? -(sideways ? width : height) / 2 : y,
            textWidth: sideways ? height : width, textHeight: sideways ? width : height,
            textTransform: angle ? `translate(${x + width / 2} ${y + height / 2}) rotate(${angle})` : null,
            polyline: points.map((point) => point.join(",")).join(" ") };
    });
}

/** A click creates a usable text area, kept inside the visible, rotated page. */
export function pdfTextBox(viewport, points, size) {
    const [start, end] = points.map((point) => viewport.convertToViewportPoint(...point));
    if (Math.abs(start[0] - end[0]) > 6 && Math.abs(start[1] - end[1]) > 6) return points;
    const width = Math.min(240 * viewport.scale, viewport.width);
    const height = Math.min(Math.max(36, size * 1.8) * viewport.scale, viewport.height);
    const x = Math.max(0, Math.min(start[0], viewport.width - width));
    const y = Math.max(0, Math.min(start[1], viewport.height - height));
    return [viewport.convertToPdfPoint(x, y), viewport.convertToPdfPoint(x + width, y + height)];
}

/** Area removal includes draft/saved notes touched on the same source page. */
export function pdfMarkInArea(mark, area) {
    if (mark.page !== area.page || mark.kind === "redact") return false;
    const bounds = (points) => [Math.min(...points.map(([x]) => x)), Math.min(...points.map(([, y]) => y)),
        Math.max(...points.map(([x]) => x)), Math.max(...points.map(([, y]) => y))];
    const [left, bottom, right, top] = bounds(mark.points), [x1, y1, x2, y2] = bounds(area.points);
    if (mark.kind === 'callout') {
        const [cx,cy] = mark.points[0];
        if (Math.hypot(cx-Math.max(x1,Math.min(x2,cx)),cy-Math.max(y1,Math.min(y2,cy))) <= pdfCalloutRadius(mark)) return true;
    }
    if (!(left <= x2 && right >= x1 && bottom <= y2 && top >= y1)) return false;
    if (!["arrow","callout","pen"].includes(mark.kind)) return true;
    return mark.points.slice(1).some((end, index) => {
        const start = mark.points[index];
        let from = 0, to = 1;
        for (const [axis, min, max] of [[0, x1, x2], [1, y1, y2]]) {
            const delta = end[axis] - start[axis];
            if (!delta) { if (start[axis] < min || start[axis] > max) return false; }
            else {
                const a = (min - start[axis]) / delta, b = (max - start[axis]) / delta;
                from = Math.max(from, Math.min(a, b)); to = Math.min(to, Math.max(a, b));
                if (from > to) return false;
            }
        }
        return true;
    });
}
